import Section from '@packages/sections/section';
import { disableBodyScroll, enableBodyScroll } from '@/helpers/bodyScrollLockWrapper';

const selectors = {
    search: '[data-search-button]',
    searchContainer: '[data-search-container]',
    searchCloseBtn: '[data-search-close]',
    header: '#shopify-section-header',
    submenu: '.has-submenu',
};

const classes = {
    active: 'active',
    open: 'open',
    notSticky: 'header-not-sticky'
};

class HeaderSection extends Section {
    async onLoad() {
        await super.onLoad();

        this.initSearch();
        this.initDesktopMenu();
        this.initSticky();

        await this.initDropdownMenus();
        await this.initDropdownMenusSecondary();
    }

    initSticky() {
        this.scrollCheckpoint = null;
        this.setStickyClass();
        window.addEventListener('scroll', this.setStickyClass.bind(this));
    }

    setStickyClass() {
        const header = document.querySelector(selectors.header);
        if (!header) {
            return;
        }

        const top = header.getBoundingClientRect().top;

        const height = header.clientHeight;

        if (top <= -1 && this.scrollCheckpoint === null) {
            this.scrollCheckpoint = window.scrollY;
        }

        if (this.scrollCheckpoint !== null && window.scrollY >= this.scrollCheckpoint + height) {
            header.classList.remove(classes.notSticky);
            return;
        }

        header.classList.add(classes.notSticky);
    }

    initDesktopMenu() {
        this.initDesktopMenuTriggers();
    }

    initDesktopMenuTriggers() {
        const triggers = document.querySelectorAll('.header__megamenu-tab_nav-item');
        if (!triggers) {
            return;
        }

        if (!triggers?.length) {
            return;
        }

        triggers?.forEach((trigger) => {
            trigger?.addEventListener('click', () => {
                triggers?.forEach((trigger) => {
                    trigger?.classList?.remove(classes?.active);
                });

                trigger?.classList?.add(classes?.active);

                const items = trigger?.parentNode?.parentNode?.querySelectorAll('.header__megamenu-tab');

                items?.forEach((item) => {
                    item?.classList?.remove(classes?.open);
                });

                const control = trigger?.dataset?.control;
                if (!control) {
                    return;
                }

                const controlElement = document.getElementById(control);
                if (!controlElement) {
                    return;
                }

                controlElement?.classList?.add(classes?.open);
            });
        });
    }

    async initDropdownMenus() {
        const container = this.container;
        if (!container) {
            return;
        }

        const menus_with_dropdown = document.querySelectorAll('.has_megamenu');
        if (!menus_with_dropdown) {
            return;
        }

        if (!menus_with_dropdown?.length) {
            return;
        }

        const { default: hoverintent } = await import('hoverintent');
        if (!hoverintent) {
            return;
        }

        const dropdownLinks = document.querySelectorAll('.dropdown-trigger');


        menus_with_dropdown?.forEach((menu_with_dropdown) => {
            hoverintent(menu_with_dropdown, () => {
                menus_with_dropdown?.forEach((dropdown) => {
                    dropdown?.classList?.remove(classes?.open);
                });

                menu_with_dropdown?.classList?.add(classes?.open);

                document.dispatchEvent(new Event('predictive-search:close'));
            }, () => {
                menu_with_dropdown?.classList?.remove(classes?.open);

                dropdownLinks?.forEach((link) => {
                    link?.classList?.remove(classes?.open);
                });
            })?.options({
                timeout: 100,
                handleFocus: true,
            });
        });


        const dropdownSubLinks = document.querySelectorAll('.dropdown-sublinks .dropdown-trigger');
        if (!dropdownLinks) {
            return;
        }

        if (!dropdownLinks?.length) {
            return;
        }

        dropdownSubLinks?.forEach((link) => {
            link?.addEventListener('click', (event) => {
                if (!!event?.target?.matches('a')) {
                    return;
                }

                dropdownLinks?.forEach((otherLink) => {
                    if (otherLink === link) {
                        return;
                    }

                    otherLink?.classList?.remove(classes?.open);
                });

                link?.classList?.toggle(classes?.open);
            });
        });
    }

    async initDropdownMenusSecondary() {
        const container = this.container;
        if (!container) {
            return;
        }

        const menus_with_dropdown = document.querySelectorAll(selectors.submenu);
        if (!menus_with_dropdown) {
            return;
        }

        if (!menus_with_dropdown?.length) {
            return;
        }

        const { default: hoverintent } = await import('hoverintent');
        if (!hoverintent) {
            return;
        }

        document.body.addEventListener('click', (e) => {
            const target = e.target;
            if (!target) {
                return;
            }

            if (window.innerWidth >= 1440) {
                return;
            }

            const menu_with_dropdown = !!target.closest(selectors.submenu);
            if (menu_with_dropdown) {
                return;
            }

            menus_with_dropdown?.forEach((dropdown) => {
                dropdown?.classList?.remove('open');
            });
        });

        menus_with_dropdown?.forEach((menu_with_dropdown) => {
            menu_with_dropdown.addEventListener('click', (e) => {
                if (window.innerWidth >= 1440) {
                    return;
                }

                const target = e.target;
                if (!target) {
                    return;
                }

                const submenu = !!target.closest('.header-secondary__submenu');
                if (submenu) {
                    return;
                }

                e.preventDefault();

                const is_open = menu_with_dropdown.classList.contains('open');

                menus_with_dropdown?.forEach((dropdown) => {
                    dropdown?.classList?.remove('open');
                });

                if (is_open) {
                    return;
                }

                menu_with_dropdown.classList.add('open');
            });

            hoverintent(menu_with_dropdown, () => {
                if (window.innerWidth < 1440) {
                    return;
                }

                menus_with_dropdown?.forEach((dropdown) => {
                    dropdown?.classList?.remove('open');
                });

                menu_with_dropdown?.classList?.add('open');
            }, () => {
                if (window.innerWidth < 1440) {
                    return;
                }

                menu_with_dropdown?.classList?.remove('open');
            })?.options({
                timeout: 100,
                handleFocus: true
            });
        });
    }

    initSearch() {
        const search = document.querySelector(selectors?.search);
        const searchContainer = document.querySelector(selectors?.searchContainer);
        if (!search || !searchContainer) {
            return;
        }

        let scrollDisabled = false;

        document.addEventListener('open:search', (event) => {
            event?.preventDefault();
            event?.stopPropagation();
            event?.stopImmediatePropagation();

            searchContainer?.classList?.add('open');

            document?.body?.classList?.add('overflow-hidden');

            if (window.innerWidth < 1440) {
                disableBodyScroll(searchContainer, {
                    allowTouchMove(el) {
                        while (el && el !== document?.body) {
                            if (el.getAttribute('data-allow-scroll') !== null) {
                                return true;
                            }

                            el = el?.parentElement;
                        }

                        return false;
                    },
                });

                scrollDisabled = true;
            }

            const searchInput = searchContainer?.querySelector('.header__search-input');
            if (!searchInput) {
                return;
            }

            searchInput?.focus();
        });

        search?.addEventListener('click', (event) => {
            event?.preventDefault();

            searchContainer?.classList?.add('open');
            document?.body?.classList?.add('overflow-hidden');

            if (window.innerWidth < 1440) {
                disableBodyScroll(searchContainer, {
                    allowTouchMove(el) {
                        while (el && el !== document?.body) {
                            if (el.getAttribute('data-allow-scroll') !== null) {
                                return true;
                            }

                            el = el?.parentElement;
                        }

                        return false;
                    },
                });

                scrollDisabled = true;
            }

            const searchInput = searchContainer?.querySelector('.header__search-input');
            if (!searchInput) {
                return;
            }

            searchInput?.focus();
        });

        document.body.addEventListener('click', (event) => {
            if (!searchContainer?.classList?.contains('open')) {
                return;
            }

            const searchBtn = event?.target?.closest(selectors?.search);
            if (searchBtn) {
                return;
            }

            const withinSearchContainer = event?.target?.closest(selectors?.searchContainer);
            if (withinSearchContainer) {
                return;
            }

            searchContainer?.classList?.remove('open');
            document?.body?.classList?.remove('overflow-hidden');

            enableBodyScroll(searchContainer);
            scrollDisabled = false;
        });

        window.addEventListener('resize', () => {
            const sContainer = document.querySelector(selectors?.searchContainer);
            if (!sContainer.classList.contains('open')) {
                return;
            }

            if (window.innerWidth < 1440) {
                if (scrollDisabled) {
                    return;
                }

                disableBodyScroll(sContainer, {
                    allowTouchMove(el) {
                        while (el && el !== document?.body) {
                            if (el.getAttribute('data-allow-scroll') !== null) {
                                return true;
                            }

                            el = el?.parentElement;
                        }

                        return false;
                    },
                });

                scrollDisabled = true;
                return;
            }

            if (!scrollDisabled) {
                return;
            }

            enableBodyScroll(searchContainer);
            scrollDisabled = false;
        });

        document.body.addEventListener('click', (event) => {
            const target = event.target;
            if (!target) {
                return;
            }

            const outerArea = !Boolean(target.closest('predictive-search')) && Boolean(target.closest(selectors.searchContainer));
            const closeBtn = Boolean(target.closest(selectors.searchCloseBtn));

            if (outerArea || closeBtn) {
                if (closeBtn) {
                    event.preventDefault();
                }

                searchContainer?.classList?.remove('open');

                document.body.classList.remove('overflow-hidden');
    
                enableBodyScroll(searchContainer);
                scrollDisabled = false;
            }
        });
    }
}

export default HeaderSection;
